import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  inject,
  input,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FeatureFlagsService } from '../../../core/common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../core/common/constants/feature-flags.constants';
import { LookupFieldComponent } from '../lookup-field/lookup-field.component';
import {
  AssignList,
  LookupFilter,
  LookupOptionItem,
} from '../lookup-field/lookup-field-item';
import {
  NG_VALUE_ACCESSOR,
  ControlContainer,
  FormGroup,
  FormArray,
  FormControl,
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ValidationMessageComponent } from '../validation-message/validation-message.component';
import { AddIconComponent, CloseIconComponent } from '@kuiper/icons';
import { TriggerAction } from '../../../form-builder/interface/request-form-builder.model';

@Component({
  selector: 'lib-multi-lookup-field',
  imports: [
    LookupFieldComponent,
    ReactiveFormsModule,
    ValidationMessageComponent,
    AddIconComponent,
    CloseIconComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './multi-lookup-field.component.html',
  styleUrl: './multi-lookup-field.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LookupFieldComponent),
      multi: true,
    },
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MultiLookupFieldComponent implements OnInit {
  private fb = inject(FormBuilder);
  private parentContainer = inject(ControlContainer);
  private readonly featureFlagService = inject(FeatureFlagsService);

  readonly parentAndChildApproachFlag = signal<boolean>(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.parent_and_child_approach
    )
  ).asReadonly();

  inputFormControlName = input.required<string>();
  id = input.required<string>();
  name = input.required<string>();

  label = input<string>('Label');
  placeholder = input<string>('Placeholder');
  searchPlaceholder = input<string>('Type to search');
  noSearchResultLabel = input<string>('No Data');
  detailsNotFoundLabel = input<string>('N/A');
  addNewAssignLabel = input<string>('Add New');
  selectAllLabel = input<string>('Select All');
  enableSelectAll = input<boolean>(false);
  viewOnly = input<boolean>(false);
  required = input<boolean>(false);
  apiUrl = input<string>('');
  filters = input<LookupFilter[]>([]);
  multiple = input<boolean>(true);
  lookupClassContainer = input<string>('group_item_content gap-20');
  lookupClass = input<string>('lookup-input');
  lang = input<string>('en');
  size = input<{ min_size: number; max_size: number } | null>(null);
  triggerAction = input<TriggerAction | null>(null);
  parentFieldKey = input<string | null>(null);
  searchKeywordKey = input<string>('search');

  selectedOption = model<LookupOptionItem[] | undefined>(undefined);
  labelValueAssignList = model.required<AssignList>();

  /** Shared header (label + close) when parent/child approach is on. */
  readonly showSharedHeader = computed(
    () => this.parentAndChildApproachFlag() && !!this.label()
  );

  /** Per-field label is suppressed when the shared header owns the label. */
  readonly fieldLabel = computed(() =>
    this.parentAndChildApproachFlag() ? '' : this.label()
  );

  /** Trigger CTA is only passed through when parent/child approach is on. */
  readonly effectiveTriggerAction = computed(() =>
    this.parentAndChildApproachFlag() ? this.triggerAction() : null
  );

  /** Icon already provides the plus; strip a leading "+" from translated labels. */
  readonly addNewButtonText = computed(() =>
    this.addNewAssignLabel().replace(/^\s*\+\s*/, '').trim()
  );

  /** True after every remaining option has been selected (Select All). */
  readonly allOptionsSelected = signal(false);

  selectedKeyValue = input<'value' | 'id'>('value');
  selectedKeyName = input<'label' | 'name'>('label');

  lookupDefaultItem: {
    item: LookupOptionItem;
    selectedOption: LookupOptionItem | undefined;
  } = {
    item: {
      label: '',
      value: '',
      [this.selectedKeyName()]: '',
      [this.selectedKeyValue()]: '',
    },
    selectedOption: undefined,
  };
  lookupList: {
    id: number;
    item: LookupOptionItem;
    selectedOption: LookupOptionItem | undefined;
  }[] = [];

  private get parentFormGroup(): FormGroup {
    return this.parentContainer.control as FormGroup;
  }

  protected get childFormArray(): FormArray {
    return this.parentFormGroup.controls[
      this.inputFormControlName()
    ] as FormArray;
  }

  ngOnInit(): void {
    if (!this.selectedOption()?.length) {
      this.lookupList[0] = {
        id: 0,
        ...this.lookupDefaultItem,
      };
    } else {
      this.selectedOption()?.forEach((item, index) => {
        this.lookupList[index] = {
          id: index,
          item: item,
          selectedOption: item,
        };
      });
    }

    const controls = this.lookupList.map(
      item =>
        new FormControl(
          item.selectedOption ? item.selectedOption?.value : null,
          this.required() ? [Validators.required] : []
        )
    );
    const formArray = this.fb.array(controls);

    this.parentFormGroup.setControl(this.inputFormControlName(), formArray);
  }

  addLookupOption() {
    this.childFormArray.push(
      new FormControl(null, this.required() ? [Validators.required] : [])
    );
    this.lookupList.push({
      id: this.lookupList.length - 1,
      ...this.lookupDefaultItem,
    });
  }

  selectOption(item: LookupOptionItem, index: number) {
    this.lookupList[index].item = item;
    this.lookupList[index].selectedOption = item;
  }

  /** Fills remaining slots with the emitted dropdown options, honoring max size. */
  selectAll(items: LookupOptionItem[]): void {
    if (!this.enableSelectAll() || !items.length) {
      return;
    }

    const alreadySelectedKeys = new Set(
      this.lookupList
        .filter(lookup => !!lookup.selectedOption)
        .map(lookup => String(this.optionKey(lookup.selectedOption!)))
    );
    const maxSize = this.size()?.max_size;
    const remainingSlots = maxSize
      ? Math.max(0, maxSize - alreadySelectedKeys.size)
      : items.length;

    const itemsToSelect = items
      .filter(item => !alreadySelectedKeys.has(String(this.optionKey(item))))
      .slice(0, remainingSlots);

    if (!itemsToSelect.length) {
      return;
    }

    const pendingIndex = this.lookupList.findIndex(
      lookup => !lookup.selectedOption
    );
    const validators = this.required() ? [Validators.required] : [];
    let nextId = this.nextLookupId();

    itemsToSelect.forEach((item, index) => {
      if (index === 0 && pendingIndex !== -1) {
        this.lookupList[pendingIndex] = {
          id: this.lookupList[pendingIndex].id,
          item,
          selectedOption: item,
        };
        this.childFormArray.at(pendingIndex).setValue(this.optionKey(item));
        return;
      }

      this.lookupList.push({
        id: nextId++,
        item,
        selectedOption: item,
      });
      this.childFormArray.push(
        new FormControl(this.optionKey(item), validators)
      );
    });

    this.syncAllOptionsSelected();
  }

  private optionKey(item: LookupOptionItem): string | number {
    return (item[this.selectedKeyValue()] ?? item.value) as string | number;
  }

  private nextLookupId(): number {
    return (
      this.lookupList.reduce(
        (maxId, lookup) => Math.max(maxId, lookup.id),
        -1
      ) + 1
    );
  }

  private selectedCount(): number {
    return this.lookupList.filter(lookup => !!lookup.selectedOption).length;
  }

  /**
   * Select All emits every remaining option (capped by max size).
   * If we are still under max size, there is nothing left to add.
   */
  private syncAllOptionsSelected(): void {
    const maxSize = this.size()?.max_size;
    const selectedCount = this.selectedCount();
    this.allOptionsSelected.set(
      selectedCount > 0 && (maxSize == null || selectedCount < maxSize)
    );
  }

  resetValue() {
    this.allOptionsSelected.set(false);
    this.lookupList[0] = {
      id: 0,
      ...this.lookupDefaultItem,
    };
    this.childFormArray.at(0).reset();

    if (this.lookupList.length === 2) {
      this.deleteLookupOption(1);
    }
  }

  deleteLookupOption(index: number) {
    this.allOptionsSelected.set(false);
    this.lookupList.splice(index, 1);
    this.childFormArray.removeAt(index);
  }

  /** True when there is a pending (unselected) lookup field that can be removed. */
  hasUnselectedLookupField(): boolean {
    return this.lookupList.some(lookup => !lookup.selectedOption);
  }

  /** Close X on shared header: pending field exists, more than one slot, not view-only. */
  showRemoveUnselected(): boolean {
    return (
      this.parentAndChildApproachFlag() &&
      this.hasUnselectedLookupField() &&
      this.lookupList.length > 1 &&
      !this.viewOnly()
    );
  }

  /** Add New enabled when last slot has a value, max size is not reached, and options remain. */
  canAddNew(): boolean {
    const lastItemValue =
      this.lookupList[this.lookupList.length - 1]?.item[this.selectedKeyValue()];
    const isMaxSizeReached = this.size()
      ? this.lookupList.length >= this.size()!.max_size
      : false;
    return !!lastItemValue && !isMaxSizeReached && !this.allOptionsSelected();
  }

  /** Removes the pending unselected lookup field (not a selected one). */
  removeUnselectedLookupField(): void {
    const unselectedIndex = this.lookupList.findIndex(
      lookup => !lookup.selectedOption
    );
    if (unselectedIndex === -1 || this.lookupList.length <= 1) {
      return;
    }
    this.deleteLookupOption(unselectedIndex);
  }

  /** Called by (clearOption) from lib-lookup-field's refresh/delete icon on a selected item.
   *  Removes that selected field when multiple exist; otherwise clears the selection. */
  handleClearLookup(index: number) {
    if (this.lookupList.length > 1) {
      this.deleteLookupOption(index);
      return;
    }
    this.allOptionsSelected.set(false);
    this.lookupList[0] = { id: 0, ...this.lookupDefaultItem };
    this.childFormArray.at(0).reset();
  }
}
